import { Injectable } from '@angular/core';
import { TokenResponse } from 'projects/api-swagger-client/src';

export interface Credentials extends TokenResponse {
  username: string;
  remember: string;
  accessToken: string;
  client_id: string;
  refreshToken: string;
}

const credentialsKey = 'credentials';

/**
 * Payload del access token (AuthUser serializado por el backend).
 * Fuente de verdad para rol y permisos en la UI.
 */
export interface DecodedAuthUser {
  id?: number;
  username?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  role?: string;
  permissions?: string[];
  companyId?: string | number;
  departmentId?: string | number;
  exp?: number;
  [key: string]: unknown;
}

/**
 * Provides storage for authentication credentials.
 * The Credentials interface should be replaced with proper implementation.
 */
@Injectable({
  providedIn: 'root',
})
export class CredentialsService {
  private _credentials: Credentials | null = null;

  constructor() {
    const savedCredentials = sessionStorage.getItem(credentialsKey) || localStorage.getItem(credentialsKey);
    if (savedCredentials) {
      this._credentials = JSON.parse(savedCredentials);
    }
  }

  /**
   * Checks is the user is authenticated.
   * @return True if the user is authenticated.
   */
  isAuthenticated(): boolean {
    if (!this.credentials?.accessToken) {
      return false;
    }

    // Check if token is expired
    try {
      const tokenPayload = this.parseJwt(this.credentials.accessToken);
      if (tokenPayload && tokenPayload.exp) {
        const currentTime = Math.floor(Date.now() / 1000);
        if (tokenPayload.exp < currentTime) {
          // Token is expired
          return false;
        }
      }
    } catch (e) {
      // If we can't parse the token, assume it's invalid
      return false;
    }

    return true;
  }

  /**
   * Parses a JWT token to extract its payload
   * @param token The JWT token to parse
   * @return The token payload or null if parsing fails
   */
  private parseJwt(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (e) {
      return null;
    }
  }

  /**
   * Returns the decoded access token payload (role, permissions, ids)
   * or null if there is no valid token.
   */
  getDecodedToken(): DecodedAuthUser | null {
    if (!this.credentials?.accessToken) {
      return null;
    }
    return this.parseJwt(this.credentials.accessToken);
  }

  /**
   * Gets the user credentials.
   * @return The user credentials or null if the user is not authenticated.
   */
  get credentials(): Credentials | null {
    return this._credentials;
  }

  /**
   * Sets the user credentials.
   * The credentials may be persisted across sessions by setting the `remember` parameter to true.
   * Otherwise, the credentials are only persisted for the current session.
   * @param credentials The user credentials.
   * @param remember True to remember credentials across sessions.
   */
  setCredentials(credentials?: Credentials) {
    this._credentials = credentials || null;
    this.storageCredentials(credentials);
  }

  /**
   * Sustituye los tokens de la sesión actual tras un cambio de empresa
   * (select-company / return-home-company).
   */
  applyTokenResponse(tokenResponse: TokenResponse) {
    const credentials = this.credentials;
    if (!credentials) {
      return;
    }
    this.setCredentials({
      ...credentials,
      accessToken: tokenResponse.accessToken!,
      refreshToken: tokenResponse.refreshToken!,
      userId: tokenResponse.userId!,
      firstName: tokenResponse.firstName!,
    });
  }

  updateCredentials(newData: any) {
    const currentCredentials = this.credentials;
    const updatedCredentials = { ...currentCredentials, ...newData };
    this.storageCredentials(updatedCredentials);
    this._credentials = updatedCredentials || null;
  }

  storageCredentials = (credentials: Credentials | any) => {
    if (credentials) {
      const storage = credentials?.remember ? localStorage : sessionStorage;
      storage.setItem(credentialsKey, JSON.stringify(credentials));
    } else {
      sessionStorage.removeItem(credentialsKey);
      localStorage.removeItem(credentialsKey);
       this.clearStorageData();
    }
  };
  private clearStorageData() {
  const keysToRemove = ['tempToken', 'availableCompanies'];
  keysToRemove.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
}
}
